<script>

import {mapActions, mapGetters} from "vuex";
import {AlertService} from "@/services/AlertService";
import requestLoaderMixin from "@/mixins/requestLoaderMixin";
import EmailModule from "@/components/settings/emails/EmailModule.vue";
import ModuleFeature from "@/components/settings/emails/ModuleFeature.vue";

export default {
  name: "EmailDetails",
  components: {ModuleFeature, EmailModule},
  mixins : [
    requestLoaderMixin
  ],
  data() {
    return {
      toEdit : false,
      email_address : '',
      is_system_email : 0,
      modules : [],
      toEditFeatureSettings : false
    }
  },
  methods : {
    ...mapActions('EmailModule', [
      'getEmailById',
      'updateEmailById',
    ]),
    ...mapActions('ModuleTableModule', [
      'getModuleKeys'
    ]),
    editEmail() {
      this.toEdit = true;
      this.email_address = this.viewedEmail?.email_address;
      this.is_system_email = this.viewedEmail?.is_system_email;
      this.modules = this.viewedEmail.modules.map(item => item.code)
    },
    async cancelEdit() {
      if (this.hasChanges === true) {
        if (await AlertService.cancelAlert()) {
          this.toEdit = false;
          this.email_address = '';
          this.is_system_email = 0;
          this.modules = [];
        }
      } else {
        this.toEdit = false;
        this.email_address = '';
        this.is_system_email = 0;
        this.modules = [];
      }

    },
    redirectToEmailsList() {
      this.$router.push({name: 'SettingEmails'});
    },
    redirectToEnabledModule() {
      const moduleCodes = this.viewedEmail.modules.map(module => module.code);
      if (moduleCodes.includes(this.$route.params.active_module) === false) {
        this.changeActiveModule(moduleCodes[0])
      }
    },
    async updateEmail() {
      if (await AlertService.confirmUpdateAlert('Are you sure you want to update?')) {
        await this.showLoaderRequest('UPDATING EMAIL, PLEASE WAIT...');

        let updateResponse = await this.updateEmailById({
          email_id : this.$route.params.email_id,
          email_address : this.email_address,
          is_system_email : this.is_system_email === true ? 1 : 0,
          modules : this.modules
        })

        if (updateResponse.result === false) {
          return false;
        }
        await this.getEmailById(this.$route.params.email_id)
        await AlertService.successAlert('Email Updated successfully', 'UPDATING OF EMAIL')
        this.cancelEdit();
        this.redirectToEnabledModule();
      }
    },
    async changeActiveModule(module) {
      if (this.$route.params.active_module === module) {
        return;
      }

      const params = this.$route.params;
      params.active_module = module;

      await this.$router.replace({name: this.$route.name, params: params});
    },
    editFeatureSettings() {
      this.toEditFeatureSettings = true;
      this.$refs.moduleFeature.forEach((feature) => {
        feature.setDisabledFields(false);
      });
    },
    async cancelEditFeatureSettings() {
      if (this.moduleFeatureHasChanges === true) {
        if (await AlertService.cancelAlert()) {
          this.toEditFeatureSettings = false;
          this.$refs.moduleFeature.forEach((feature) => {
            feature.setDisabledFields(true);
            feature.initialize();
          });
        }
      } else {
        this.toEditFeatureSettings = false;
        this.$refs.moduleFeature.forEach((feature) => {
          feature.setDisabledFields(true);
          feature.initialize();
        });
      }

    },
    async saveFeatureSettings() {
      if (await AlertService.confirmUpdateAlert('Are you sure you want to update?')) {
        await this.showLoaderRequest('UPDATING EMAIL MODULE FEATURE, PLEASE WAIT...');
        for (let counter = 0; counter < this.$refs.moduleFeature.length; counter++) {
          if (this.$refs.moduleFeature[counter].canClickSaveButton) {
            await this.$refs.moduleFeature[counter].saveChanges();
          }
          await this.$refs.moduleFeature[counter].setDisabledFields(true);
        }
        AlertService.successAlert('Email Module Feature successfully updated!', 'UPDATING OF EMAIL FEATURES')
        this.toEditFeatureSettings = false;
      }
    }
  },
  async created() {
    await this.getEmailById(this.$route.params.email_id)
    await this.getModuleKeys();
    await this.redirectToEnabledModule();
  },
  computed: {
    ...mapGetters('ModuleTableModule', [
      'moduleKeys'
    ]),
    ...mapGetters('EmailModule', [
      'viewedEmail'
    ]),
    disableSaveButton() {
      return this.email_address.trim() === '' || this.modules.length === 0;
    },
    hasChanges() {
      if (this.toEdit === false) {
        return false;
      }
      const viewModulesCodes = this.viewedEmail.modules.map(item => item.code)
      const sameEmailAddress = this.viewedEmail.email_address.trim() !== this.email_address.trim();
      const sameIsSystemSettings = this.viewedEmail.is_system_email !== this.is_system_email;
      const sameModules = this.modules?.length !== this.viewedEmail.modules?.length ||
        [...this.modules].sort().join() !== [...viewModulesCodes].sort().join();


      return sameEmailAddress || sameIsSystemSettings || sameModules;
    },
    activeEmailModuleFeatures() {
      return this.viewedEmail?.modules.filter((module) => {
        return module.code === this.$route.params.active_module
      })[0]?.features ?? []
    },
    moduleFeatureHasChanges() {
      return this.$refs.moduleFeature?.some(child => child.canClickSaveButton);
    }
  },
  async beforeRouteLeave(to, from, next) {
    if (this.hasChanges === true || this.moduleFeatureHasChanges === true) {
      if (await AlertService.cancelChanges()) {
        next();
      } else {
        next(false);
      }

    } else {
      next();
    }
  },
}
</script>

<template>
  <div>
    <div class="row">
      <div class="col-12 pl-0">
        <h3 class="text-left">Email Settings</h3>

        <div class="card p-3 shadow-sm">
          <div class="row mb-1">
            <div class="col-12">
              <div class="row justify-content-between">
                <div class="col-auto">
                </div>
                <div class="col-auto">
                  <template v-if="toEdit">
                    <button class="e-btn e-btn-outlined-red ml-2" type="button" @click="cancelEdit">
                      Cancel
                    </button>
                    <button
                      class="e-btn e-btn-blue ml-2"
                      type="submit"
                      :class="hasChanges === false || disableSaveButton ? 'e-btn-blue-disabled' : ''"
                      :disabled="hasChanges === false || disableSaveButton"
                      @click="updateEmail">
                      Save
                    </button>
                  </template>

                  <template v-else>
                    <button class="mr-1 float-right px-2 py-1 e-btn-blue-gray force-white-all rounded" type="button" @click="redirectToEmailsList"
                    >
                      <font-awesome-icon icon="chevron-left" class="e-text-black"/> Go Back
                    </button>
                    <button class="e-btn e-btn-green mr-1" type="button" @click="editEmail">
                      Edit Email
                    </button>
                  </template>



                </div>
              </div>
            </div>
          </div>

          <div class="row align-items-center no-gutters mb-1">
            <div class="col-lg-3">
              <label class="font-weight-bolder mb-0">Email Address:</label>
            </div>
            <div class="col-lg-9" v-if="toEdit">
              <input type="text" v-model="email_address" id="email_address" class="form-control form-control-sm" required>
            </div>
            <div class="col-lg-9" v-else>
              {{ viewedEmail?.email_address }}
            </div>
          </div>

          <div class="row align-items-center no-gutters mb-1">
            <div class="col-lg-3">
              <label class="font-weight-bolder mb-0">Is System Email <span class="text-danger">(For monitoring):</span></label>
            </div>
            <div class="col-lg-9" v-if="toEdit">
              <input class="checkbox-input" type="checkbox" :checked="is_system_email === 1" v-model="is_system_email">
            </div>
            <div class="col-lg-9" v-else>
              {{ viewedEmail?.is_system_email === 1 ? 'YES' : 'NO' }}
            </div>
          </div>

          <hr class="m-0">
          <div class="row align-items-center no-gutters mb-1">
            <div class="col-lg-3">
              <label class="font-weight-bolder mb-0">Modules:</label>
            </div>
            <div class="col-lg-9 p-0" v-if="toEdit">
              <div class="form-check" v-for="module in moduleKeys">
                <input class="form-check-input checkbox-input" type="checkbox" :value="module.code" :id="module.code" v-model="modules">
                <label :for="module.code" class="module_label font-weight-bold col-form-label no-select cursor-pointer">{{ module.name }}</label>
              </div>

<!--              <input type="text" v-model="email_address" id="email_address" class="form-control form-control-sm" required>-->
            </div>
            <div class="col-lg-9 p-0" v-else>
              <div class="form-check" v-for="module in moduleKeys">
                <input disabled class="form-check-input checkbox-input" type="checkbox" :checked="viewedEmail.modules.map(item => item.id).includes(module.id)">
                <label :for="module.code" class="module_label font-weight-bold col-form-label no-select cursor-pointer">{{ module.name }}</label>
              </div>
            </div>
          </div>
        </div>

        <div class="col-12 pl-0 mt-2">
          <nav>
            <div class="nav nav-tabs" id="nav-tab" role="tablist">
              <template v-for="module in moduleKeys">
                <button
                  :disabled="viewedEmail?.modules?.map(item => item.code).includes(module.code) === false"
                  :class="[
                    $route.params.active_module === module.code ? 'active' : '',
                    viewedEmail?.modules?.map(item => item.code).includes(module.code) === false ? 'disabled-type' : ''
                    ]"
                  class="main-tabs nav-link text-uppercase font-weight-bold"
                  data-toggle="pill"
                  type="button"
                  role="tab"
                  @click="changeActiveModule(module.code)">
                  {{ module.name }}
                </button>
              </template>
            </div>
          </nav>
        </div>


        <div class="card shadow-sm p-3">
          <div class="row mb-1">
            <div class="col-12">
              <div class="row justify-content-between">
                <div class="col-auto">
                </div>
                <div class="col-auto">
                  <template v-if="toEditFeatureSettings">
                    <button class="e-btn e-btn-outlined-red ml-2" type="button" @click="cancelEditFeatureSettings">
                      Cancel
                    </button>
                    <button
                      class="e-btn e-btn-blue ml-2"
                      type="submit"
                      :class="moduleFeatureHasChanges === false ? 'e-btn-blue-disabled' : ''"
                      :disabled="moduleFeatureHasChanges === false"
                      @click="saveFeatureSettings">
                      Save
                    </button>
                  </template>

                  <template v-else>
                    <button class="e-btn e-btn-green mr-1" type="button" @click="editFeatureSettings">
                      Edit Feature Settings
                    </button>
                  </template>



                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <template v-for="feature in activeEmailModuleFeatures">
              <module-feature
                ref="moduleFeature"
                :key="`${$route.params.email_id}_${feature.name}`"
                :feature="feature"
              />
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.no-bullet {
  list-style-type: none;
}

.nav-tabs .nav-link.active {
  color: #d3313c !important;
}
</style>
